import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, User, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_explore-trailz/artifacts/t85u1s3d_Trailz_logo_without_text-removebg-preview.png" 
              alt="Trailz Logo" 
              className="h-16 w-16 object-contain"
            />
            <div className={`text-3xl font-serif font-bold transition-colors ${
              isScrolled ? 'text-emerald-900' : 'text-white drop-shadow-lg'
            }`}>
              Trailz
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`font-medium transition-colors hover:text-amber-600 ${
                isScrolled ? 'text-zinc-700' : 'text-white'
              }`}
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection('tours')}
              className={`font-medium transition-colors hover:text-amber-600 ${
                isScrolled ? 'text-zinc-700' : 'text-white'
              }`}
            >
              Our Journeys
            </button>
            <Link 
              to="/build-adventure" 
              className={`font-medium transition-colors hover:text-amber-600 ${
                isScrolled ? 'text-zinc-700' : 'text-white'
              }`}
            >
              Build Your Adventure
            </Link>
            <Link 
              to="/blog" 
              className={`font-medium transition-colors hover:text-amber-600 ${
                isScrolled ? 'text-zinc-700' : 'text-white'
              }`}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className={`font-medium transition-colors hover:text-amber-600 ${
                isScrolled ? 'text-zinc-700' : 'text-white'
              }`}
            >
              Contact
            </button>
            <Button 
              size="sm"
              className={`rounded-sm transition-all duration-300 ${
                isScrolled 
                  ? 'bg-emerald-800 hover:bg-emerald-900 text-white' 
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
              onClick={() => scrollToSection('contact')}
            >
              Plan Your Trip
            </Button>
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/portal'}>
                <Button
                  size="sm"
                  variant="outline"
                  className={`rounded-sm transition-all duration-300 ${
                    isScrolled
                      ? 'border-emerald-800 text-emerald-800 hover:bg-emerald-50'
                      : 'border-white text-white hover:bg-white/10'
                  }`}
                  data-testid="header-portal-btn"
                >
                  {user.role === 'admin' ? <Shield className="w-4 h-4 mr-1.5" /> : <User className="w-4 h-4 mr-1.5" />}
                  {user.role === 'admin' ? 'Admin' : 'My Portal'}
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button
                  size="sm"
                  variant="outline"
                  className={`rounded-sm transition-all duration-300 ${
                    isScrolled
                      ? 'border-emerald-800 text-emerald-800 hover:bg-emerald-50'
                      : 'border-white text-white hover:bg-white/10'
                  }`}
                  data-testid="header-login-btn"
                >
                  <User className="w-4 h-4 mr-1.5" />
                  Sign In
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden transition-colors ${
              isScrolled ? 'text-zinc-900' : 'text-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-zinc-200 shadow-lg">
          <nav className="px-4 py-6 space-y-4">
            <Link 
              to="/" 
              className="block text-zinc-700 hover:text-amber-600 font-medium"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection('tours')}
              className="block w-full text-left text-zinc-700 hover:text-amber-600 font-medium"
            >
              Our Journeys
            </button>
            <Link 
              to="/build-adventure" 
              className="block text-zinc-700 hover:text-amber-600 font-medium"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Build Your Adventure
            </Link>
            <Link 
              to="/blog" 
              className="block text-zinc-700 hover:text-amber-600 font-medium"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-zinc-700 hover:text-amber-600 font-medium"
            >
              Contact
            </button>
            <Button 
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white rounded-sm"
              onClick={() => scrollToSection('contact')}
            >
              Plan Your Trip
            </Button>
            {user ? (
              <Link
                to={user.role === 'admin' ? '/admin' : '/portal'}
                className="block text-zinc-700 hover:text-amber-600 font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-portal-link"
              >
                {user.role === 'admin' ? 'Admin Dashboard' : 'My Portal'}
              </Link>
            ) : (
              <Link
                to="/login"
                className="block text-zinc-700 hover:text-amber-600 font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-login-link"
              >
                Sign In
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;